import { inject, Injectable } from '@angular/core';
import { transaction } from '@datorama/akita';
import { AbstractFeatureChildService, getUniqueIdNamePair } from '@y42/feature';
import { METADATA_JSON_SCHEMA_URL, Y42MetadataGitModel } from '@y42/feature/json-schema';
import { Y42RouteDetailTabs, Y42RouterService, Y42Routes } from '@y42/feature/routing';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import {
  VisualizationDashboardQuery,
  VisualizationDashboardStore,
  VisualizationDashboardStoreModel,
} from '../../state/dashboard';
import {
  VisualizationTabQuery,
  VisualizationTabState,
  VisualizationTabStore,
  VisualizationTabStoreModel,
} from '../../state/tab';
import { VisualizationTabGitFactoryService } from '../../state/tab/visualization-tab-git-factory.service';
import { VisualizationCanvasElementService } from '../canvas-element/visualization-canvas-element.service';

@Injectable({ providedIn: 'root' })
export class VisualizationTabService extends AbstractFeatureChildService<VisualizationTabState> {
  constructor(
    private readonly parentStore: VisualizationDashboardStore,
    private readonly parentQuery: VisualizationDashboardQuery,
    private readonly query: VisualizationTabQuery,
    private readonly store: VisualizationTabStore,
    private readonly routerService: Y42RouterService,
    private readonly canvasElementService: VisualizationCanvasElementService,
  ) {
    super({
      feature: Y42FeatureType.VisualizationDashboard,
      gitFactory: inject(VisualizationTabGitFactoryService),
      parentStore,
      parentQuery,
      store,
      query,
    });
  }

  async create(name: string, options: TabCreationOptions = {}): Promise<VisualizationTabStoreModel> {
    const dashboard = options.parentDashboard ?? this.parentQuery.getActive();

    assertNonNullable(dashboard);

    const tab = await this.createTabEntity(name, dashboard);

    this.parentStore.update(dashboard.$path, {
      settings: { ...dashboard.settings, order: [...dashboard.settings.order, tab.$path] },
    });

    if (!options.skipNavigation) {
      await this.navigateToTab(tab);
    }

    return tab;
  }

  async duplicate(
    $path: VisualizationTabStoreModel['$path'],
    options: TabCreationOptions = {},
  ): Promise<VisualizationTabStoreModel> {
    const originalTab = this.query.getEntity($path);
    if (!originalTab) {
      throw new Error(`[Visualization-Tab]: cannot duplicate empty tab`);
    }

    const dashboard = options.parentDashboard ?? this.parentQuery.getActive();
    assertNonNullable(dashboard);

    const newTab = await this.createTabEntity(originalTab.name, dashboard);

    const $children = await this.canvasElementService.duplicate({
      paths: originalTab.$children,
      parentTab: newTab.$path,
      parentDashboardName: dashboard.$name,
    });

    this.store.update(newTab.$path, {
      $children,
    });

    // Do not wait for this.store.pending as the number of duplicated
    // canvas element may be quite big and this operation blocks git service for some time.

    const order = [...dashboard.settings.order];

    const tabOrderIndex = order.findIndex((tabPath) => tabPath === $path);
    order.splice(tabOrderIndex + 1, 0, newTab.$path);
    this.parentStore.update(dashboard.$path, {
      settings: { ...dashboard.settings, order },
    });

    // Do not wait for this.parentStore.pending as the number of duplicated
    // canvas element may be quite big and this operation blocks git service for some time.

    if (!options.skipNavigation) {
      await this.navigateToTab(newTab);
    }

    return newTab;
  }

  async update(
    tab: Partial<VisualizationTabStoreModel>,
    $path: VisualizationTabStoreModel['$path'] = this.query.getActiveId()!,
  ): Promise<void> {
    if (!$path) {
      throw new Error(`[Visualization-Tab]: "${$path}" not found.`);
    }

    this.store.update($path, tab);
    await this.store.pending;
  }

  async delete($path: VisualizationTabStoreModel['$path']): Promise<void> {
    const tabs = this.parentQuery.getActiveUIModel()?.tabs || [];

    if (tabs.length <= 1) {
      throw new Error(`[Visualization-Tab]: cannot remove tab, at least one tab needs to exist.`);
    }

    const activeDashboard = this.parentQuery.getActive()!;
    this.store.delete($path);
    this.parentStore.updateActiveWithoutSync((st) => {
      const childrenWithoutDeletedPath = (st.$children || []).filter((childPath) => childPath !== $path);
      return { $children: childrenWithoutDeletedPath };
    });

    const deleteIndex = tabs.findIndex((t) => t && t.$path === $path);

    // Remove the path from the order array
    this.parentStore.updateActive({
      ...activeDashboard,
      settings: {
        ...activeDashboard.settings,
        order: activeDashboard.settings.order.filter((tabPath) => tabPath !== $path),
      },
    });
    await this.store.pending;

    const updatedTabs = this.parentQuery.getActiveUIModel()?.tabs || [];
    const newActiveTabIndex = Math.min(updatedTabs.length - 1, deleteIndex);

    const newActiveTab = updatedTabs[newActiveTabIndex];
    assertNonNullable(newActiveTab);
    await this.navigateToTab(newActiveTab);
  }

  async rename($path: VisualizationTabStoreModel['$path'], name: string) {
    this.store.update($path, { name });
    await this.store.pending;
  }

  @transaction()
  async reorderTab(previousIndex: number, currentIndex: number) {
    const activeDashboard = this.parentQuery.getActive();
    if (!activeDashboard) {
      return;
    }
    const order = [...(activeDashboard?.settings.order || [])];
    const splicedItem = order.splice(previousIndex, 1)[0];
    assertNonNullable(splicedItem);
    order.splice(currentIndex, 0, splicedItem);
    this.parentStore.updateActive({
      ...activeDashboard,
      settings: { ...activeDashboard.settings, order },
    });
  }

  toggleEditMode() {
    this.store.update((state) => ({
      ui: {
        ...state.ui,
        editMode: !state.ui.editMode,
        // Close the sidebar in the view mode.
        sidebar: {
          ...state.ui.sidebar,
          ...(state.ui.editMode ? { open: false } : {}),
        },
      },
    }));
  }

  setEditMode(value: boolean) {
    this.store.update((state) => ({
      ui: {
        ...state.ui,
        editMode: value,
        // Close the sidebar in the view mode.
        sidebar: {
          ...state.ui.sidebar,
          ...(!value ? { open: false } : {}),
        },
      },
    }));
  }

  toggleSidebar({ type, open }: { type?: 'filter' | 'theme'; open?: boolean }) {
    this.store.update((state) => ({
      ui: {
        ...state.ui,
        sidebar: {
          open: open ?? !state.ui.sidebar.open,
          type,
        },
      },
    }));
  }

  private async createTabEntity(tabName: string, dashboard: VisualizationDashboardStoreModel) {
    const { name: safeName, id } = getUniqueIdNamePair({
      name: tabName,
      query: this.query,
    });

    const tab = this.store.create({
      ...new Y42MetadataGitModel(),
      $schema: METADATA_JSON_SCHEMA_URL,
      $parent: dashboard.$path,
      $name: id,
      name: safeName,
      $parentFolders: [dashboard.$name],
      description: '',
    });

    return tab;
  }

  private async navigateToTab(tab: VisualizationTabStoreModel) {
    const tabParent = tab.$parentFolders[0];
    assertNonNullable(tabParent);
    await this.routerService.navigateToDetail(
      Y42Routes.VisualizationDashboard,
      tabParent,
      Y42RouteDetailTabs.Overview,
      tab.$name,
    );
  }
}

interface TabCreationOptions {
  skipNavigation?: boolean;
  parentDashboard?: VisualizationDashboardStoreModel;
}
